import { variablePillClasses } from '@/components/rich-text-editor/variables/variable-pill-classes';

import { MessageVariable } from '@/shared/constants/message-constants';

const TOKEN_PATTERN = /\{[a-z_]+\}/g;

/** Single-line variant of `resolveMessageVariables`: null values become '', tokens absent from
 *  the map are left as-is, then whitespace runs collapse and the result is trimmed. */
export function resolveTemplateVariables(
	template: string,
	valuesByToken: Record<string, string | null | undefined>
): string {
	const values = Object.fromEntries(
		Object.entries(valuesByToken).map(([token, value]) => [token, value ?? ''])
	);
	return resolveMessageVariables(template, values, { blankUnknown: false })
		.replace(/\s+/g, ' ')
		.trim();
}

/** Tokens the app can't fill (the server sends each recipient their own link), left as typed. */
const SERVER_ONLY_TOKENS = new Set(['{assignment_link}']);

/** Message variant of `resolveTemplateVariables`: keeps whitespace. Unknown tokens become '' when
 *  `blankUnknown` (inserting a saved template, matching the server's bulk send) and are left as typed
 *  otherwise (live typing). */
export function resolveMessageVariables(
	text: string,
	valuesByToken: Record<string, string>,
	{ blankUnknown }: { blankUnknown: boolean }
): string {
	return text.replace(TOKEN_PATTERN, (token) => {
		if (token in valuesByToken) return valuesByToken[token];
		return blankUnknown && !SERVER_ONLY_TOKENS.has(token) ? '' : token;
	});
}

/** Fills known tokens as the user types and returns where the caret lands; null when nothing changed. */
export function resolveMessageVariablesAtCursor(
	text: string,
	cursorPos: number,
	valuesByToken: Record<string, string>
): { text: string; cursorPos: number } | null {
	const resolved = resolveMessageVariables(text, valuesByToken, { blankUnknown: false });
	if (resolved === text) return null;
	const beforeCursor = resolveMessageVariables(text.slice(0, cursorPos), valuesByToken, {
		blankUnknown: false,
	});
	return { text: resolved, cursorPos: beforeCursor.length };
}

export function findUnknownTokens(template: string, allowedVariables: MessageVariable[]): string[] {
	const allowedTokens = new Set(allowedVariables.map((variable) => variable.token));
	return [...new Set(template.match(TOKEN_PATTERN) ?? [])].filter(
		(token) => !allowedTokens.has(token)
	);
}

function escapeHtml(text: string): string {
	return text
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;');
}

function escapeRegExp(text: string): string {
	return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Byte-for-byte what `VariableNode.renderHTML` emits, so a seeded message that is sent unedited
 *  is identical to one the editor produced. Attribute order and the `{label}` text both matter. */
function variableNodeHtml(variable: MessageVariable): string {
	return (
		`<span data-id="${variable.id}" data-token="${variable.token}" data-label="${variable.label}"` +
		` data-type="variable" class="${variablePillClasses}">{${variable.label}}</span>`
	);
}

/** Converts a plain-text `{token}` template into the HTML the rich-text editor
 *  parses into variable pills. Inverse of `parseHtmlToPlainText` on editor output:
 *  known tokens become `<span data-type="variable">` nodes, literals are escaped. */
export function tokensToEditorHtml(template: string, allowedVariables: MessageVariable[]): string {
	if (!template) return '';
	if (allowedVariables.length === 0) return escapeHtml(template);

	const variableByToken = new Map(allowedVariables.map((variable) => [variable.token, variable]));
	const pattern = new RegExp(
		`(${allowedVariables.map((v) => escapeRegExp(v.token)).join('|')})`,
		'g'
	);

	return template
		.split(pattern)
		.map((part) => {
			const variable = variableByToken.get(part);
			if (!variable) return escapeHtml(part);
			return variableNodeHtml(variable);
		})
		.join('');
}
